import React from 'react';
import { BRAND } from '../../../config/region';

/*
 * "about-us" in every language it has been translated into.
 *
 * Shared fields (slug, dates) sit at the top level; each language supplies only what differs.
 * Russian is the authored original. A language that is missing here falls back to English, or to
 * Russian in the russia build — see blogPosts/index.js.
 *
 * Every language, Russian included, says {BRAND} rather than a hardcoded name. The Russian text is
 * not russia-only: the europe build serves Russian too, at plotex.io/ru/, so a literal "AI Novel"
 * there would put the wrong company's name on Plotex pages.
 */
const post = {
  slug: 'about-us',
  datePublished: '2025-02-01',
  dateModified: '2025-06-01',

  russian: {
    title: '🌌 О нас',
    description: `${BRAND} — интерактивные новеллы с ИИ, где ты становишься соавтором истории.`,
    seoTitle: `О нас — ${BRAND}`,
    seoDescription: `${BRAND} — платформа интерактивных визуальных новелл с ИИ. Мы стартап, создающий уникальные интерактивные истории с искусственным интеллектом.`,
    content: (
      <>
        <p>
          {BRAND} — интерактивные новеллы с искусственным интеллектом, где ты
          становишься полноценным соавтором истории, а не просто игроком.
        </p>
        <p>
          Мы — стартап, родившийся из любви к интерактивным историям и желания
          создавать что-то по-настоящему уникальное. В феврале 2025 года мы
          запустились с простого Telegram-бота — хотели дать людям возможность
          попробовать интерактивные новеллы в удобном формате.
        </p>
        <p>
          Сегодня мы выросли в полноценную платформу: у нас есть сайт и
          мини-приложение в Telegram. Но мы не останавливаемся — мы продолжаем
          развиваться, улучшать продукт и создавать то, что вдохновляет и авторов,
          и игроков.
        </p>
        <h2>Стань кем угодно</h2>
        <p>
          Забудь о своём привычном мире. Здесь ты можешь стать кем угодно —
          отважным исследователем неизведанных земель, хитрым детективом в нуарном
          городе, древним магом или будущим учёным. Выбери свою судьбу и проживи
          жизнь, о которой всегда мечтал.
        </p>
        <h2>Автор и игрок — одно целое</h2>
        <p>
          В наших новеллах есть два неделимых элемента — автор и игрок. Без твоего
          погружения не получится интересной новеллы. Здесь недостаточно просто
          нажимать на кнопки — нужно стать частью мира и его соавтором. Обещаю,
          тогда он подарит тебе незабываемые впечатления!
        </p>
        <h2>Правил не существует</h2>
        <p>
          Тебе необязательно следовать по задуманному маршруту. Делай что хочешь:
          можно играть по сюжету, можно сломать все правила и проверить границы
          этого мира, можно выбрать одного персонажа и общаться только с ним. Мы
          лишь задали общую рамку этой книги и очертили основные главы, а наполняешь
          эту вселенную событиями и диалогами только ты!
        </p>
      </>
    ),
  },

  english: {
    title: '🌌 About us',
    description: `${BRAND} — AI-powered interactive novels where you become a co-author of the story.`,
    seoTitle: `About us — ${BRAND}`,
    seoDescription: `${BRAND} is a platform for AI-powered interactive visual novels. We are a startup building interactive stories where the characters answer you back.`,
    content: (
      <>
        <p>
          {BRAND} is interactive novels with artificial intelligence, where you become a full
          co-author of the story rather than just a player.
        </p>
        <p>
          We are a startup born out of a love for interactive stories and a wish to build
          something genuinely new. In February 2025 we launched with a simple Telegram bot — we
          wanted to give people a way to try interactive novels in a format that fits into
          their day.
        </p>
        <p>
          Since then we have grown into a full platform. But we are not stopping here: we keep
          developing, improving the product and building things that inspire authors and
          players alike.
        </p>
        <h2>Become anyone</h2>
        <p>
          Forget the world you are used to. Here you can become anyone — a fearless explorer of
          uncharted lands, a sharp detective in a noir city, an ancient mage or a scientist of
          the future. Choose your fate and live the life you always imagined.
        </p>
        <h2>Author and player are one</h2>
        <p>
          Our novels have two inseparable parts: the author and the player. Without your
          immersion there is no interesting novel. Pressing buttons is not enough — you have to
          become part of the world and its co-author. Do that, and it will give you something
          you will not forget.
        </p>
        <h2>There are no rules</h2>
        <p>
          You do not have to follow the route anyone planned. Do what you like: play the story
          straight, break every rule and test the limits of the world, or pick one character and
          talk only to them. We only set the frame of this book and sketched its main chapters —
          the events and dialogue that fill the universe are entirely yours.
        </p>
      </>
    ),
  },

  german: {
    title: '🌌 Über uns',
    description: `${BRAND} — interaktive Novellen mit KI, in denen du zum Mitautor der Geschichte wirst.`,
    seoTitle: `Über uns — ${BRAND}`,
    seoDescription: `${BRAND} ist eine Plattform für interaktive Visual Novels mit KI. Wir sind ein Startup und bauen interaktive Geschichten, in denen dir die Figuren wirklich antworten.`,
    content: (
      <>
        <p>
          {BRAND} sind interaktive Novellen mit künstlicher Intelligenz, in denen du zum
          vollwertigen Mitautor der Geschichte wirst und nicht nur Spieler bleibst.
        </p>
        <p>
          Wir sind ein Startup, entstanden aus der Liebe zu interaktiven Geschichten und dem
          Wunsch, etwas wirklich Eigenes zu bauen. Im Februar 2025 sind wir mit einem einfachen
          Telegram-Bot gestartet — wir wollten Menschen die Möglichkeit geben, interaktive
          Novellen in einem Format auszuprobieren, das in ihren Alltag passt.
        </p>
        <p>
          Inzwischen sind wir zu einer vollwertigen Plattform gewachsen. Aber wir bleiben nicht
          stehen: Wir entwickeln weiter, verbessern das Produkt und schaffen Dinge, die Autoren
          wie Spieler begeistern.
        </p>
        <h2>Werde, wer du willst</h2>
        <p>
          Vergiss die Welt, die du kennst. Hier kannst du jeder sein — furchtlose Entdeckerin
          unbekannter Länder, scharfsinniger Detektiv in einer Noir-Stadt, uralter Magier oder
          Wissenschaftlerin der Zukunft. Wähle dein Schicksal und lebe das Leben, von dem du
          immer geträumt hast.
        </p>
        <h2>Autor und Spieler sind eins</h2>
        <p>
          Unsere Novellen haben zwei untrennbare Teile: den Autor und den Spieler. Ohne dein
          Eintauchen entsteht keine spannende Novelle. Knöpfe drücken reicht hier nicht — du
          musst Teil der Welt und ihr Mitautor werden. Tu das, und sie schenkt dir etwas, das du
          nicht vergisst.
        </p>
        <h2>Es gibt keine Regeln</h2>
        <p>
          Du musst keiner vorgezeichneten Route folgen. Mach, was du willst: spiele die
          Geschichte geradeaus, brich jede Regel und teste die Grenzen dieser Welt, oder such dir
          eine einzige Figur aus und rede nur mit ihr. Wir haben nur den Rahmen dieses Buches
          gesetzt und die Hauptkapitel skizziert — die Ereignisse und Dialoge, die dieses
          Universum füllen, kommen allein von dir.
        </p>
      </>
    ),
  },

  french: {
    title: '🌌 À propos',
    description: `${BRAND} — des romans interactifs avec IA où tu deviens coauteur de l'histoire.`,
    seoTitle: `À propos — ${BRAND}`,
    seoDescription: `${BRAND} est une plateforme de romans visuels interactifs avec IA. Nous sommes une startup qui crée des histoires interactives dont les personnages te répondent vraiment.`,
    content: (
      <>
        <p>
          {BRAND}, ce sont des romans interactifs avec intelligence artificielle, où tu deviens
          pleinement coauteur de l&apos;histoire et pas seulement joueur.
        </p>
        <p>
          Nous sommes une startup née d&apos;un amour pour les histoires interactives et de
          l&apos;envie de créer quelque chose de vraiment différent. En février 2025, nous avons
          lancé un simple bot Telegram — nous voulions permettre aux gens d&apos;essayer les
          romans interactifs dans un format qui tienne dans leur journée.
        </p>
        <p>
          Depuis, nous sommes devenus une véritable plateforme. Mais nous ne nous arrêtons pas
          là : nous continuons à évoluer, à améliorer le produit et à créer ce qui inspire les
          auteurs comme les joueurs.
        </p>
        <h2>Deviens qui tu veux</h2>
        <p>
          Oublie le monde qui t&apos;est familier. Ici, tu peux devenir n&apos;importe qui —
          exploratrice intrépide de terres inconnues, détective rusé dans une ville noire, mage
          ancien ou scientifique du futur. Choisis ton destin et vis la vie dont tu as toujours
          rêvé.
        </p>
        <h2>Auteur et joueur ne font qu&apos;un</h2>
        <p>
          Nos romans reposent sur deux éléments inséparables : l&apos;auteur et le joueur. Sans
          ton immersion, il n&apos;y a pas de roman intéressant. Appuyer sur des boutons ne
          suffit pas — il faut devenir une partie du monde et son coauteur. Fais-le, et il
          t&apos;offrira quelque chose que tu n&apos;oublieras pas.
        </p>
        <h2>Il n&apos;y a pas de règles</h2>
        <p>
          Tu n&apos;es pas obligé de suivre l&apos;itinéraire prévu. Fais ce que tu veux : suis
          l&apos;intrigue, brise toutes les règles et teste les limites de ce monde, ou choisis
          un seul personnage et ne parle qu&apos;à lui. Nous n&apos;avons posé que le cadre de ce
          livre et esquissé ses grands chapitres — les événements et les dialogues qui
          remplissent cet univers ne viennent que de toi.
        </p>
      </>
    ),
  },

  spanish: {
    title: '🌌 Sobre nosotros',
    description: `${BRAND}: novelas interactivas con IA donde te conviertes en coautor de la historia.`,
    seoTitle: `Sobre nosotros — ${BRAND}`,
    seoDescription: `${BRAND} es una plataforma de novelas visuales interactivas con IA. Somos una startup que crea historias interactivas en las que los personajes te responden de verdad.`,
    content: (
      <>
        <p>
          {BRAND} son novelas interactivas con inteligencia artificial, donde te conviertes en
          coautor pleno de la historia y no solo en jugador.
        </p>
        <p>
          Somos una startup nacida del amor por las historias interactivas y del deseo de crear
          algo realmente distinto. En febrero de 2025 empezamos con un sencillo bot de Telegram:
          queríamos dar a la gente la posibilidad de probar novelas interactivas en un formato
          que cupiera en su día a día.
        </p>
        <p>
          Desde entonces hemos crecido hasta ser una plataforma completa. Pero no nos detenemos:
          seguimos desarrollando, mejorando el producto y creando cosas que inspiran tanto a
          autores como a jugadores.
        </p>
        <h2>Conviértete en quien quieras</h2>
        <p>
          Olvida el mundo al que estás acostumbrado. Aquí puedes ser cualquiera: una exploradora
          intrépida de tierras desconocidas, un detective astuto en una ciudad noir, un mago
          antiguo o una científica del futuro. Elige tu destino y vive la vida que siempre
          imaginaste.
        </p>
        <h2>Autor y jugador son uno</h2>
        <p>
          Nuestras novelas tienen dos partes inseparables: el autor y el jugador. Sin tu
          inmersión no hay novela interesante. Aquí no basta con pulsar botones: hay que formar
          parte del mundo y ser su coautor. Hazlo y te dará algo que no vas a olvidar.
        </p>
        <h2>No existen las reglas</h2>
        <p>
          No tienes por qué seguir la ruta prevista. Haz lo que quieras: juega siguiendo la
          trama, rompe todas las reglas y pon a prueba los límites de este mundo, o elige un
          único personaje y habla solo con él. Nosotros solo hemos puesto el marco de este libro
          y esbozado sus capítulos principales: los acontecimientos y los diálogos que llenan
          este universo los pones tú.
        </p>
      </>
    ),
  },

  italian: {
    title: '🌌 Chi siamo',
    description: `${BRAND} — romanzi interattivi con IA in cui diventi coautore della storia.`,
    seoTitle: `Chi siamo — ${BRAND}`,
    seoDescription: `${BRAND} è una piattaforma di romanzi visivi interattivi con IA. Siamo una startup che crea storie interattive in cui i personaggi ti rispondono davvero.`,
    content: (
      <>
        <p>
          {BRAND} sono romanzi interattivi con intelligenza artificiale, in cui diventi coautore
          a tutti gli effetti della storia e non solo un giocatore.
        </p>
        <p>
          Siamo una startup nata dall&apos;amore per le storie interattive e dal desiderio di
          costruire qualcosa di davvero nuovo. A febbraio 2025 siamo partiti con un semplice bot
          Telegram: volevamo dare alle persone la possibilità di provare i romanzi interattivi in
          un formato che entrasse nella loro giornata.
        </p>
        <p>
          Da allora siamo cresciuti fino a diventare una piattaforma completa. Ma non ci
          fermiamo: continuiamo a evolvere, a migliorare il prodotto e a creare cose che ispirano
          tanto gli autori quanto i giocatori.
        </p>
        <h2>Diventa chi vuoi</h2>
        <p>
          Dimentica il mondo a cui sei abituato. Qui puoi essere chiunque: un&apos;esploratrice
          coraggiosa di terre sconosciute, un detective astuto in una città noir, un antico mago
          o una scienziata del futuro. Scegli il tuo destino e vivi la vita che hai sempre
          immaginato.
        </p>
        <h2>Autore e giocatore sono una cosa sola</h2>
        <p>
          I nostri romanzi hanno due parti inseparabili: l&apos;autore e il giocatore. Senza il
          tuo coinvolgimento non nasce un romanzo interessante. Premere pulsanti non basta:
          devi diventare parte del mondo e suo coautore. Fallo, e ti darà qualcosa che non
          dimenticherai.
        </p>
        <h2>Non esistono regole</h2>
        <p>
          Non sei obbligato a seguire il percorso previsto. Fai quello che vuoi: segui la trama,
          infrangi ogni regola e metti alla prova i limiti di questo mondo, oppure scegli un solo
          personaggio e parla soltanto con lui. Noi abbiamo solo definito la cornice di questo
          libro e tracciato i capitoli principali: gli eventi e i dialoghi che riempiono questo
          universo li metti tu.
        </p>
      </>
    ),
  },

  polish: {
    title: '🌌 O nas',
    description: `${BRAND} — interaktywne powieści z AI, w których stajesz się współautorem historii.`,
    seoTitle: `O nas — ${BRAND}`,
    seoDescription: `${BRAND} to platforma interaktywnych powieści wizualnych z AI. Jesteśmy startupem tworzącym interaktywne historie, w których postacie naprawdę Ci odpowiadają.`,
    content: (
      <>
        <p>
          {BRAND} to interaktywne powieści ze sztuczną inteligencją, w których stajesz się
          pełnoprawnym współautorem historii, a nie tylko graczem.
        </p>
        <p>
          Jesteśmy startupem, który narodził się z miłości do interaktywnych historii i chęci
          stworzenia czegoś naprawdę własnego. W lutym 2025 roku wystartowaliśmy z prostym botem
          na Telegramie — chcieliśmy dać ludziom możliwość spróbowania interaktywnych powieści w
          formacie, który mieści się w codziennym dniu.
        </p>
        <p>
          Od tego czasu wyrośliśmy na pełnoprawną platformę. Ale nie zatrzymujemy się: wciąż się
          rozwijamy, ulepszamy produkt i tworzymy rzeczy, które inspirują zarówno autorów, jak i
          graczy.
        </p>
        <h2>Zostań kimkolwiek</h2>
        <p>
          Zapomnij o świecie, który znasz. Tutaj możesz być kimkolwiek — nieustraszoną
          odkrywczynią nieznanych lądów, sprytnym detektywem w mrocznym mieście, starożytnym
          magiem albo naukowczynią przyszłości. Wybierz swój los i przeżyj życie, o jakim zawsze
          marzyłeś.
        </p>
        <h2>Autor i gracz to jedno</h2>
        <p>
          Nasze powieści mają dwie nierozłączne części: autora i gracza. Bez Twojego zaangażowania
          nie powstanie ciekawa powieść. Klikanie przycisków tu nie wystarczy — trzeba stać się
          częścią świata i jego współautorem. Zrób to, a odwdzięczy Ci się czymś, czego nie
          zapomnisz.
        </p>
        <h2>Nie ma żadnych zasad</h2>
        <p>
          Nie musisz podążać wyznaczoną trasą. Rób, co chcesz: graj zgodnie z fabułą, złam
          wszystkie zasady i sprawdź granice tego świata albo wybierz jedną postać i rozmawiaj
          tylko z nią. My wyznaczyliśmy jedynie ramy tej książki i naszkicowaliśmy główne
          rozdziały — wydarzenia i dialogi, które wypełniają ten wszechświat, pochodzą wyłącznie
          od Ciebie.
        </p>
      </>
    ),
  },

  portuguese: {
    title: '🌌 Sobre nós',
    description: `${BRAND} — novels interativas com IA em que você se torna coautor da história.`,
    seoTitle: `Sobre nós — ${BRAND}`,
    seoDescription: `${BRAND} é uma plataforma de visual novels interativas com IA. Somos uma startup que cria histórias interativas em que os personagens respondem a você de verdade.`,
    content: (
      <>
        <p>
          {BRAND} são novels interativas com inteligência artificial, em que você se torna um
          verdadeiro coautor da história, e não apenas um jogador.
        </p>
        <p>
          Somos uma startup que nasceu do amor por histórias interativas e da vontade de criar
          algo realmente novo. Em fevereiro de 2025, começamos com um simples bot no Telegram —
          queríamos dar às pessoas uma forma de experimentar novels interativas num formato que
          cabe no dia a dia.
        </p>
        <p>
          Desde então, crescemos e viramos uma plataforma completa. Mas não vamos parar por aqui:
          seguimos evoluindo, melhorando o produto e criando coisas que inspiram tanto autores
          quanto jogadores.
        </p>
        <h2>Seja quem você quiser</h2>
        <p>
          Esqueça o mundo a que você está acostumado. Aqui você pode ser qualquer pessoa — uma
          exploradora destemida de terras desconhecidas, um detetive astuto numa cidade noir, um
          mago ancestral ou uma cientista do futuro. Escolha o seu destino e viva a vida que você
          sempre imaginou.
        </p>
        <h2>Autor e jogador são um só</h2>
        <p>
          Nossas novels têm duas partes inseparáveis: o autor e o jogador. Sem a sua imersão, não
          existe novel interessante. Apertar botões não basta — é preciso se tornar parte do
          mundo e seu coautor. Faça isso, e ele vai te dar algo que você não vai esquecer.
        </p>
        <h2>Não existem regras</h2>
        <p>
          Você não precisa seguir o caminho que alguém planejou. Faça o que quiser: siga a
          história à risca, quebre todas as regras e teste os limites do mundo, ou escolha um
          único personagem e converse só com ele. Nós apenas definimos a moldura deste livro e
          esboçamos seus capítulos principais — os acontecimentos e diálogos que preenchem esse
          universo são inteiramente seus.
        </p>
      </>
    ),
  },
};

export default post;
